import { useState, useEffect } from 'react';
import { NavLink, Navigate, Outlet, useLocation } from 'react-router';
import { GraduationCap, LogOut, MoreHorizontal, User, ChevronDown } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { navForRole, type NavItem } from './roleNav';
import { Switcher } from './Switcher';
import { ConnectionStatus } from './ConnectionStatus';
import { ErrorBoundary } from './ErrorBoundary';
import { trackRouteView, trackNavEvent } from '../observability/clarity';

const ROLE_LABELS: Record<string, string> = {
  SchoolAdmin: 'Admin', Teacher: 'Teacher', Accountant: 'Accountant',
  Parent: 'Parent', Student: 'Student', SuperAdmin: 'Platform',
};

function SidebarLink({ item }: { item: NavItem }) {
  const location = useLocation();
  const hasChildren = item.children && item.children.length > 0;
  const isActive = hasChildren
    ? item.children!.some((c) => location.pathname === c.to)
    : location.pathname === item.to;
  const [expanded, setExpanded] = useState(isActive);

  useEffect(() => {
    if (isActive) setExpanded(true);
  }, [isActive]);

  if (hasChildren) {
    return (
      <div>
        <button
          type="button"
          className={`sidebar-link${isActive ? ' active' : ''}`}
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          <span aria-hidden="true" className="nav-link-icon">{item.icon}</span>
          <span style={{ flex: 1 }}>{item.label}</span>
          <ChevronDown
            size={16}
            aria-hidden="true"
            style={{ transition: 'transform var(--motion-fast)', transform: expanded ? 'rotate(180deg)' : undefined }}
          />
        </button>
        {expanded && (
          <div style={{ paddingLeft: 'var(--space-4)' }}>
            {item.children!.map((child) => (
              <NavLink
                key={child.to}
                to={child.to}
                end
                className={({ isActive: childActive }) => `sidebar-link${childActive ? ' active' : ''}`}
                onClick={() => trackNavEvent('nav-primary-clicked', child.to)}
              >
                <span aria-hidden="true" className="nav-link-icon">{child.icon}</span>
                <span>{child.label}</span>
              </NavLink>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <NavLink to={item.to} end className={({ isActive }) => `sidebar-link${isActive ? ' active' : ''}`}
      onClick={() => trackNavEvent('nav-primary-clicked', item.to)}>
      <span aria-hidden="true" className="nav-link-icon">{item.icon}</span>
      <span>{item.label}</span>
    </NavLink>
  );
}

function BottomNavLink({ item }: { item: NavItem }) {
  return (
    <NavLink to={item.to} end className={({ isActive }) => `bottom-nav-link${isActive ? ' active' : ''}`}
      onClick={() => trackNavEvent('nav-secondary-clicked', item.to)}>
      <span aria-hidden="true" className="nav-link-icon">{item.icon}</span>
      <span>{item.label}</span>
    </NavLink>
  );
}

const MAX_BOTTOM_NAV = 5;

export function AppShell() {
  const session = useAuthStore((s) => s.session);
  const expire = useAuthStore((s) => s.expire);
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const segment = location.pathname.replace(/^\//, '').split('/')[0] ?? '';

  // Clarity route tracking: emit on every SPA navigation.
  useEffect(() => {
    const path = location.pathname;
    const isPublic = ['/login', '/signup', '/demo', '/pricing'].some((p) => path.startsWith(p));
    const isCheckout = path.startsWith('/checkout');
    const isAdmin = path.startsWith('/superadmin') || path.startsWith('/platform');
    trackRouteView({
      path,
      pageType: isPublic ? 'public' : isCheckout ? 'checkout' : isAdmin ? 'admin' : 'authenticated',
      routeGroup: segment || 'root',
    });
  }, [location.pathname, segment]);

  if (!session) return <Navigate to="/login" replace />;

  const role = session.activeRole;
  const navItems = navForRole(role);
  const overflow = navItems.length > MAX_BOTTOM_NAV;
  const primaryNav = overflow ? navItems.slice(0, MAX_BOTTOM_NAV - 1) : navItems;
  const moreNav = overflow ? navItems.slice(MAX_BOTTOM_NAV - 1) : [];
  const currentLabel =
    navItems.find((n) => n.to === location.pathname)?.label
    ?? (segment ? segment.charAt(0).toUpperCase() + segment.slice(1) : 'Home');

  return (
    <div className="app-shell">
      <a href="#page-content" className="skip-link">Skip to main content</a>

      <aside className="app-sidebar">
        <div className="sidebar-brand">
          <GraduationCap size={24} aria-hidden="true" />
          <span>mySkool</span>
        </div>
        <nav className="sidebar-nav" aria-label="Main">
          {navItems.map((n) => <SidebarLink key={n.to} item={n} />)}
        </nav>
        <div className="sidebar-footer">
          <Switcher />
          <div className="sidebar-footer-row">
            <span className="sidebar-footer-label">
              {ROLE_LABELS[role] ?? role}
            </span>
            <button
              type="button"
              onClick={expire}
              className="sidebar-footer-signout"
              aria-label="Sign out"
            >
              <LogOut size={14} aria-hidden="true" />
              <span>Sign out</span>
            </button>
          </div>
        </div>
      </aside>

      <div className="app-main">
        <header className="app-header">
          <h1 className="app-header-title">
            {currentLabel}
          </h1>
          <div className="app-header-actions">
            <ConnectionStatus />
            <span className="app-header-role">
              <User size={14} aria-hidden="true" />
              {ROLE_LABELS[role] ?? role}
            </span>
            {/* Mobile sign-out */}
            <button
              type="button"
              onClick={expire}
              className="app-header-signout-mobile"
              aria-label="Sign out"
            >
              <LogOut size={18} aria-hidden="true" />
            </button>
          </div>
        </header>
        <div className="app-content" id="page-content" data-testid="page-content" data-page={segment}>
          <ErrorBoundary key={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </div>
      </div>

      <nav className="bottom-nav" aria-label="Mobile">
        {primaryNav.map((n) => <BottomNavLink key={n.to} item={n} />)}
        {overflow && (
          <button
            type="button"
            className="bottom-nav-link"
            aria-haspopup="true"
            aria-expanded={moreOpen}
            onClick={() => setMoreOpen((v) => !v)}
          >
            <span aria-hidden="true" className="nav-link-icon">
              <MoreHorizontal size={20} />
            </span>
            <span>More</span>
          </button>
        )}
      </nav>
      {overflow && moreOpen && (
        <div className="bottom-nav-more" role="menu" aria-label="More navigation">
          {moreNav.map((n) => (
            <NavLink key={n.to} to={n.to} role="menuitem" className="bottom-nav-more-item" onClick={() => setMoreOpen(false)}>
              <span aria-hidden="true" className="nav-link-icon">{n.icon}</span>
              <span>{n.label}</span>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}
